/** Ranked play UI pieces (Phase 7J): the ready-panel offer, the results block and the development score listing. */
import { button, h } from '../editor/dom';
import { formatTime } from '../ui/Hud';
import type { RankedState } from './RankedFlow';

type Click = () => void;

const UNAVAILABLE: Record<Extract<RankedState, { kind: 'unavailable' }>['why'], string> = {
  offline: 'Ranked server unavailable: this run is local only.',
  'not-ranked': 'This track is not ranked.',
  version: 'The ranked server ranks a different version of this track or of the game: this run is local only.',
};

/** The ranked line on the ready panel; a button when a ranked attempt can be started. */
export function rankedReady(state: RankedState, enabled: boolean, onRanked: Click): HTMLElement | null {
  switch (state.kind) {
    case 'checking':
      return null;
    case 'unavailable':
      return state.why === 'not-ranked' ? null : h('p', { className: 'ranked-line', dataset: { ranked: 'unavailable' }, textContent: UNAVAILABLE[state.why] });
    case 'arming':
      return h('p', { className: 'ranked-line', dataset: { ranked: 'arming' }, textContent: 'Requesting a ranked attempt…' });
    case 'armed':
      return h('p', { className: 'ranked-line armed', dataset: { ranked: 'armed' }, textContent: `Ranked attempt ready. Finish a full run without deaths; the server will replay it and time it. Expires ${new Date(state.grant.expiresAt).toLocaleTimeString()}.` });
    case 'error':
      return h('div', { className: 'ranked-line', dataset: { ranked: 'error' } }, h('p', { textContent: `Ranked: ${state.message}` }), button('Try ranked again', onRanked, { className: 'secondary', dataset: { action: 'ranked-start' } }));
    default:
      return h(
        'div',
        { className: 'ranked-line', dataset: { ranked: 'available' } },
        button(enabled ? 'Start ranked attempt' : 'Ranked attempt', onRanked, { className: 'secondary', dataset: { action: 'ranked-start' } }),
        h('p', { className: 'panel-help', textContent: 'A ranked run is submitted when you finish; the server replays your inputs on its own copy of the track and keeps its own time.' }),
      );
  }
}

/** The ranked result of the run on the results panel. */
export function rankedResult(state: RankedState, enabled: boolean, runValid: boolean, submittedThisRun: boolean): HTMLElement | null {
  if (!enabled) return null;
  const line = (status: string, text: string, time: string | null = null): HTMLElement =>
    h('div', { className: `ranked-result ${status}`, dataset: { ranked: status }, role: 'status' }, h('p', { className: 'ranked-kicker', textContent: 'Ranked' }), time ? h('p', { className: 'ranked-time', textContent: time }) : null, h('p', { textContent: text }));
  if (!submittedThisRun && state.kind !== 'armed') return line('not-submitted', 'Not ranked: this run had no ranked attempt. Retry starts a new ranked attempt.');
  switch (state.kind) {
    case 'submitting':
      return line('submitting', 'Verifying on the server: it is replaying your run…');
    case 'decided': {
      const d = state.decision;
      const time = d.seconds !== null ? `Server time ${formatTime(d.seconds)}` : null;
      return line(d.status, d.message, time);
    }
    case 'error':
      return line('error', `Not submitted: ${state.message}`);
    case 'armed':
      return runValid ? line('submitting', 'Preparing the submission…') : line('not-submitted', 'Not submitted: only full runs without deaths can be ranked. Your attempt is still open.');
    default:
      return null;
  }
}

/** Development servers only: accepted ranked records, best first per track. */
export function devScoresScreen(rows: Record<string, unknown>[] | string, back: Click): HTMLElement {
  const body =
    typeof rows === 'string'
      ? h('p', { className: 'panel-help', dataset: { scores: 'error' }, textContent: rows })
      : rows.length === 0
        ? h('p', { className: 'panel-help', dataset: { scores: 'empty' }, textContent: 'No accepted ranked runs yet.' })
        : h(
            'table',
            { className: 'splits dev-scores', dataset: { scores: 'list' } },
            h('thead', {}, h('tr', {}, ...['Track', 'Server time', 'Player', 'Submission', 'Received'].map((t) => h('th', { textContent: t })))),
            h(
              'tbody',
              {},
              ...rows.map((r) =>
                h(
                  'tr',
                  { dataset: { submission: String(r['submission_id']) } },
                  h('td', { textContent: String(r['track_key']) }),
                  h('td', { textContent: formatTime(Number(r['seconds'])) }),
                  h('td', { textContent: String(r['player_id']) }),
                  h('td', { textContent: String(r['submission_id']) }),
                  h('td', { textContent: new Date(Number(r['received_at'])).toLocaleString() }),
                ),
              ),
            ),
          );
  return h(
    'section',
    { className: 'screen screen-scores', dataset: { screen: 'scores' } },
    h('div', { className: 'screen-bar' }, button('← Menu', back, { className: 'back', dataset: { nav: 'back' } }), h('header', { className: 'screen-head' }, h('h1', { textContent: 'Ranked scores (development)' }))),
    h('p', { className: 'panel-help', textContent: 'Accepted runs on the local development server, timed by the server. Not a public leaderboard.' }),
    body,
  );
}
